/**
 * System → MCP. Proves that Smplify exposes MCP tools and surfaces the
 * OAuth clients that have connected. Both list endpoints are P2 — when
 * the backend isn't shipping them yet, we render a polished quickstart
 * empty state pointing at the docs.
 */
import { useMemo, useState } from "react";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useMcpClients, useMcpTools } from "@/hooks/security/useMcpAdmin";
import { McpClientsTable } from "@/components/system/McpClientsTable";
import { McpToolsCatalog } from "@/components/system/McpToolsCatalog";
import { RegisterMcpClientDialog } from "@/components/system/RegisterMcpClientDialog";
import { Plug, BookOpen, ChevronDown, ChevronRight, Plus } from "lucide-react";
import type { McpClientDTO, McpToolDTO } from "@/types/edrApi";

export default function SystemMcpPage() {
  return (
    <ConnectorGate connectorId="edr">
      <Inner />
    </ConnectorGate>
  );
}

function Inner() {
  const clientsQ = useMcpClients();
  const toolsQ = useMcpTools();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  const clients: McpClientDTO[] = clientsQ.data?.clients ?? [];
  const tools: McpToolDTO[] = toolsQ.data?.tools ?? [];
  const toolsByPlane = clientsQ.data?.toolsByPlane ?? {};
  const totalCalls = clientsQ.data?.totalCalls24h ?? 0;

  const planeCount = Object.keys(toolsByPlane).length;
  const activeCount = clients.filter((c) => (c.status ?? "active") === "active").length;

  // Derive "most-called tool" and "most-active client" from the
  // server-side aggregates we already have. No client-side recomputation
  // of totals — just a max over what the API returned.
  const topTool = useMemo(() => {
    if (tools.length === 0) return null;
    return [...tools].sort(
      (a, b) => (b.callsLast24h ?? 0) - (a.callsLast24h ?? 0),
    )[0];
  }, [tools]);

  const topClient = useMemo(() => {
    if (clients.length === 0) return null;
    return [...clients].sort(
      (a, b) => (b.callsLast24h ?? 0) - (a.callsLast24h ?? 0),
    )[0];
  }, [clients]);

  return (
    <>
      <SurfaceShell
        overline="System · MCP"
        title="MCP Clients"
        summary={`${activeCount} active client${activeCount === 1 ? "" : "s"} authorized · ${tools.length} tool${tools.length === 1 ? "" : "s"} exposed across ${planeCount || 0} plane${planeCount === 1 ? "" : "s"}.`}
        error={clientsQ.isError ? clientsQ.error : undefined}
        onRetry={clientsQ.isError ? () => clientsQ.refetch() : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline">
              <a
                href="https://modelcontextprotocol.io"
                target="_blank"
                rel="noreferrer"
              >
                <BookOpen className="mr-1.5 h-3.5 w-3.5" /> How MCP works
              </a>
            </Button>
            <Button size="sm" onClick={() => setRegisterOpen(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Register client
            </Button>
          </div>
        }
        kpis={
          <>
            <StatTile
              label="Total calls (24h)"
              value={totalCalls.toLocaleString()}
              loading={clientsQ.isLoading}
              hint="Across all clients"
            />
            <StatTile
              label="Most-called tool"
              value={topTool?.name ?? "—"}
              loading={toolsQ.isLoading}
              hint={
                topTool
                  ? `${(topTool.callsLast24h ?? 0).toLocaleString()} calls`
                  : "No activity"
              }
            />
            <StatTile
              label="Most-active client"
              value={topClient?.displayName ?? "—"}
              loading={clientsQ.isLoading}
              hint={
                topClient
                  ? `${(topClient.callsLast24h ?? 0).toLocaleString()} calls`
                  : "No activity"
              }
            />
            <StatTile
              label="Revoked"
              value={
                clients.filter((c) => (c.status ?? "").toLowerCase() === "revoked").length
              }
              loading={clientsQ.isLoading}
              hint="Lifetime"
            />
          </>
        }
      >
        <Panel
          title={`Active clients · ${clients.length}`}
          subtitle="Agents and tools that have authenticated against this tenant's MCP gateway."
          loading={clientsQ.isLoading}
          isError={clientsQ.isError}
          error={clientsQ.error}
          onRetry={() => clientsQ.refetch()}
          empty={!clientsQ.isLoading && !clientsQ.isError && clients.length === 0}
          skeleton="table"
        >
          {clients.length > 0 ? (
            <McpClientsTable clients={clients} />
          ) : (
            <QuickstartEmpty onRegister={() => setRegisterOpen(true)} />
          )}
        </Panel>

        <Panel
          loading={toolsQ.isLoading}
          isError={toolsQ.isError}
          error={toolsQ.error}
          onRetry={() => toolsQ.refetch()}
          empty={!toolsQ.isLoading && !toolsQ.isError && tools.length === 0}
          skeleton="list"
        >
          {tools.length > 0 ? (
            <Collapsible open={toolsOpen} onOpenChange={setToolsOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md px-1 py-1 text-sm hover:bg-muted/40"
                >
                  <span className="flex items-center gap-2 font-medium text-foreground">
                    {toolsOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {toolsOpen ? "Hide" : "Show"} all {tools.length} tools by plane
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {Object.entries(toolsByPlane)
                      .map(([p, n]) => `${n} ${p}`)
                      .join(" · ")}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3">
                <McpToolsCatalog tools={tools} />
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <EmptyState
              icon={Plug}
              title="Tool catalog not yet published"
              description="The /admin/mcp/tools endpoint hasn't shipped yet. When it does, each plane's tools will appear here grouped by scope."
            />
          )}
        </Panel>
      </SurfaceShell>

      <RegisterMcpClientDialog open={registerOpen} onOpenChange={setRegisterOpen} />
    </>
  );
}

function QuickstartEmpty({ onRegister }: { onRegister: () => void }) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-12">
      <div className="rounded-full bg-muted/40 p-3 mb-4">
        <Plug className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-base font-semibold text-foreground">
        No MCP clients connected yet
      </h3>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Smplify is MCP-native. Connect Claude, Cursor, Codex, or any custom agent
        and they'll show up here with the scopes you grant them.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <Button asChild size="sm" variant="outline">
          <a
            href="https://docs.smplify.com/mcp/quickstart#claude"
            target="_blank"
            rel="noreferrer"
          >
            Connect Claude Code →
          </a>
        </Button>
        <Button asChild size="sm" variant="outline">
          <a
            href="https://docs.smplify.com/mcp/quickstart#cursor"
            target="_blank"
            rel="noreferrer"
          >
            Connect Cursor →
          </a>
        </Button>
        <Button size="sm" onClick={onRegister}>
          Custom client (DCR) →
        </Button>
      </div>
    </div>
  );
}
